import React, { useEffect, useState } from 'react';
import { Accordion, Button } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import ContextAwareToggle from '../../../Profile/ContextAwareToggle';
import { sanitizeHtml, isHtmlContent } from '../../../Comments/utils/sanitizeHtml';
import RichTextEditor from '../../../Comments/RichTextEditor';
import { isReadonlyRole, isInternal, isProjectStandardRole } from '../../../../redux/actions/userAction';
import { REQUEST_STATUSES } from '../../../shared/constants/RequestStatuses';
import './CommunicationFeed.css';
import { FaPen } from 'react-icons/fa';

const renderMessageContent = (message) => {
  if (isHtmlContent(message)) {
    return (
      <div
        className="communication-message communication-html"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(message) }}
      />
    );
  }
  return <div className="communication-message">{message}</div>;
};

const CommunicationFeed = ({ requestId, requestStatus }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [plainTextLength, setPlainTextLength] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editHtml, setEditHtml] = useState('');
  const [editPlainTextLength, setEditPlainTextLength] = useState(0);

  // Get current user LDAP ID
  let currentUserLdapId = '';
  try {
    const passData = JSON.parse(sessionStorage.getItem('passData'));
    currentUserLdapId = passData?.user || '';
  } catch (e) {
    /* ignore parse errors */
  }

  const fetchCommunications = () => {
    setLoading(true);
    setError(null);
    axios
      .get(
        global.REACT_APP_API_ENDPOINT +
          `/lender/request/communication/all?requestMasterTrackerId=${requestId}`
      )
      .then((response) => {
        const data = response.data || [];

        // Sort by createdDateUtc descending (fallback to createdDate if missing)
        const sorted = data.sort(
          (a, b) => new Date(b.createdDateUtc || b.createdDate) - new Date(a.createdDateUtc || a.createdDate)
        );

        setCommunications(sorted);
        
        // console.log('communication feed loaded', {
        //   requestMasterTrackerId: requestId,
        //   count: data.length,
        //   userId: currentUserLdapId,
        // });
      })
      .catch((err) => {
        setError(err);
        console.error('communication feed error', {
          requestMasterTrackerId: requestId,
          error: err.message,
          userId: currentUserLdapId,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (requestId) {
      fetchCommunications();
    }
  }, [requestId]);

  const handleSubmit = () => {
    setSubmitting(true);

    axios
      .post(
        global.REACT_APP_API_ENDPOINT +
          `/lender/request/communication?requestMasterTrackerId=${requestId}`,
        { 
          message: htmlContent, 
          isExternalUser: !isInternal(), 
          createdBy: currentUserLdapId 
        }
      )
      .then(() => {
        console.log('communication submitted', {
          requestMasterTrackerId: requestId,
          messageLength: plainTextLength,
          userId: currentUserLdapId,
        });
        setShowEditor(false);
        setHtmlContent('');
        setPlainTextLength(0);
        fetchCommunications();
      })
      .catch((err) => {
        setError({ type: 'submit', message: err.message });
        console.error('communication submit error', {
          requestMasterTrackerId: requestId,
          error: err.message,
          userId: currentUserLdapId,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleCancel = () => {
    setShowEditor(false);
    setHtmlContent('');
    setPlainTextLength(0);
    setError(null);
  };

  const getDisplayName = (comm) => {
    if (!isInternal() && comm.isExternalUser === false) {
      return 'Fannie Mae';
    }

    return comm.createdByName || comm.createdByLdapId || '(n/a)';
  }

  const canEdit = (comm) => {
    if (!isProjectStandardRole()) return false;
    if (comm.createdByLdapId !== currentUserLdapId) return false;
    const dateValue = comm.createdDateUtc || comm.createdDate;
    if (!dateValue) return false;
    const minutesElapsed = moment.utc().diff(moment.utc(dateValue), 'minutes');
    return minutesElapsed < 60;
  };

  const handleEditClick = (comm) => {
    setShowEditor(false);
    setHtmlContent('');
    setPlainTextLength(0);
    setEditingId(comm.requestCommunicationId);
    setEditHtml(comm.message);
    setEditPlainTextLength(comm.message ? comm.message.replace(/<[^>]*>/g, '').length : 0);
    setError(null);
    console.log('communication edit started', {
      requestMasterTrackerId: requestId,
      requestCommunicationId: comm.requestCommunicationId,
      userId: currentUserLdapId,
    });
  };

  const handleEditCancel = () => {
    const cancelledId = editingId;
    setEditingId(null);
    setEditHtml('');
    setEditPlainTextLength(0);
    setError(null);
    console.log('communication edit canceled', {
      requestMasterTrackerId: requestId,
      requestCommunicationId: cancelledId,
      userId: currentUserLdapId,
    });
  };

  const handleEditSave = () => {
    setSubmitting(true);
    axios
      .post(
        global.REACT_APP_API_ENDPOINT +
          `/lender/request/communication?requestMasterTrackerId=${requestId}`,
        {
          message: editHtml,
          isExternalUser: !isInternal(),
          createdBy: currentUserLdapId,
          requestCommunicationId: editingId,
        }
      )
      .then(() => {
        console.log('communication edit saved', {
          requestMasterTrackerId: requestId,
          requestCommunicationId: editingId,
          messageLength: editPlainTextLength,
          userId: currentUserLdapId,
        });
        setEditingId(null);
        setEditHtml('');
        setEditPlainTextLength(0);
        fetchCommunications();
      })
      .catch((err) => {
        setError({ type: 'edit', message: err.message });
        console.error('communication edit error', {
          requestMasterTrackerId: requestId,
          requestCommunicationId: editingId,
          error: err.message,
          userId: currentUserLdapId,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const renderContent = () => {
    if (loading) {
      return <p className="communication-loading">Loading communications...</p>;
    }

    if (error && error.type !== 'submit' && error.type !== 'edit') {
      return (
        <p className="communication-error">
          Unable to load communications.{' '}
          <button
            type="button"
            onClick={fetchCommunications}
            className="communication-retry"
          >
            Please try again.
          </button>
        </p>
      );
    }

    return (
      <div className="communication-content">
        {!isReadonlyRole() && !showEditor && (
          <div className="communication-toolbar">
            {communications.length === 0 && (
              <span className="communication-empty">No communications yet.</span>
            )}
            <Button
              className="btn-submit"
              onClick={() => { setEditingId(null); setShowEditor(true); setError(null); }}
              disabled={requestStatus === REQUEST_STATUSES.UNRESPONSIVE && !isInternal()}
            >
              Add Comment
            </Button>
          </div>
        )}

        {showEditor && (
          <div className="communication-editor-area">
            <RichTextEditor
              onChange={(html) => setHtmlContent(html)}
              onPlainTextChange={(text) => setPlainTextLength(text.length)}
              maxLength={5000}
              placeholder="Type your message..."
              ariaLabel="Communication message editor"
              error={false}
            />
            {error && error.type === 'submit' && (
              <p className="communication-error">
                Failed to send message. Please try again.
              </p>
            )}
            <div className="communication-actions">
              <Button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={plainTextLength === 0 || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
              <Button variant="outline-secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!showEditor && communications.length === 0 && isReadonlyRole() && (
          <p className="communication-empty">No communications yet.</p>
        )}

        {communications.length > 0 && (
          <div className="communication-list">
            {communications.map((comm, index) => (
              <div key={comm.requestCommunicationId || index} className={`communication-item${comm.isExternalUser === false ? ' internal' : ''}${editingId === comm.requestCommunicationId ? ' editing' : ''}`}>
                <div className="communication-header">
                  <span className="communication-author">
                    {getDisplayName(comm)}
                  </span>
                  <span className="communication-time">
                    {moment(comm.createdDateUtc || comm.createdDate).format('MM/DD/YY h:mm A')}
                  </span>
                  {canEdit(comm) && editingId === null && requestStatus !== REQUEST_STATUSES.UNRESPONSIVE && (
                    <button
                      type="button"
                      className="communication-edit-btn"
                      onClick={() => handleEditClick(comm)}
                    >
                      <FaPen className="communication-edit-icon" />
                      Edit
                    </button>
                  )}
                </div>
                {editingId === comm.requestCommunicationId ? (
                  <div className="communication-editor-area">
                    <RichTextEditor
                      initialHtml={comm.message}
                      onChange={(html) => setEditHtml(html)}
                      onPlainTextChange={(text) => setEditPlainTextLength(text.length)}
                      maxLength={5000}
                      placeholder="Edit your message..."
                      ariaLabel="Edit communication message"
                      error={false}
                    />
                    {error && error.type === 'edit' && (
                      <p className="communication-error">
                        Failed to save. Please try again.
                      </p>
                    )}
                    <div className="communication-actions">
                      <Button
                        className="btn-submit"
                        onClick={handleEditSave}
                        disabled={editPlainTextLength === 0 || submitting}
                      >
                        {submitting ? 'Saving...' : 'Save'}
                      </Button>
                      <Button variant="outline-secondary" onClick={handleEditCancel}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  renderMessageContent(comm.message)
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="request-stack">
      <Accordion defaultActiveKey="0">
        <ContextAwareToggle eventKey="1">
          <p className="request-stack-header accordion-header-title">
            Communications
          </p>
        </ContextAwareToggle>
        <Accordion.Collapse eventKey="1">
          <div>{renderContent()}</div>
        </Accordion.Collapse>
      </Accordion>
    </div>
  );
};

export default CommunicationFeed;
